import { TransactionColumnToSort } from "@/modules/finances/utils/financeUtils"
import { ColumnSortingOrder } from "@/modules/types/datatableTypes"
import { LoggerService } from "@modules/common/logger/loggerService"
import { ApiClient } from "@modules/core/api/client/apiClient"
import { type CustomerHistoryDto, CustomerHistoryDtoSchema } from "@modules/finances/gateway/customerHistoryDto"
import { type FinanceStatisticsDto, FinanceStatisticsDtoSchema } from "@modules/finances/gateway/financeStatisticsDto"
import {
  type TransactionDetailsDto,
  TransactionDetailsDtoSchema,
  type TransactionDto,
  TransactionDtoSchema,
} from "@modules/finances/gateway/transactionDto"
import { type UserFinanceApiGateway } from "@modules/finances/gateway/userFinanceApiGateway"
import { PaymentPlace, TransactionFilterStatus } from "@modules/finances/types/financesType"
import { type StripeErrorsDto } from "@modules/finances/types/stripeErrorTypes"
import { type Paginated, type PaginatedCursor, toPage } from "@modules/utils/paginated"

export class UserFinanceApi implements UserFinanceApiGateway {
  private userFinanceBaseUrl = "/financial"
  private userStripeErrorsUrl = "/stripe/errors"
  private userStripePaymentRetryUrl = "/stripe/payment/authentication"

  constructor(private api: ApiClient) {}

  async getTransaction(transactionId: string): Promise<TransactionDetailsDto> {
    const result = await this.api.get<TransactionDetailsDto>(`${this.userFinanceBaseUrl}/transactions/${transactionId}`)
    return LoggerService.parseApiDto(TransactionDetailsDtoSchema, result.data)
  }

  async refundTransaction(transactionId: string): Promise<void> {
    await this.api.post(`${this.userFinanceBaseUrl}/transactions/${transactionId}/refund`)
  }

  async getTransactions(
    filter: TransactionFilterStatus | undefined,
    paymentPlace: PaymentPlace | undefined,
    nameFilter: string | undefined,
    period: string,
    page: number,
    size = 10,
    sortBy: TransactionColumnToSort = TransactionColumnToSort.DATE,
    sortOrder: ColumnSortingOrder = ColumnSortingOrder.DESC,
  ) {
    const result = await this.api.get<Paginated<TransactionDto>>(`${this.userFinanceBaseUrl}/transactions`, {
      params: {
        page,
        size,
        period,
        status: filter,
        paymentMethodFilter: paymentPlace,
        search: nameFilter,
        sort: sortBy.split(",").map((s) => `${s},${sortOrder}`),
      },
      paramsSerializer: {
        indexes: null,
      },
    })

    const pageData = toPage(result.data)

    return {
      ...pageData,
      content: pageData.content.map((transaction) => LoggerService.parseApiDto(TransactionDtoSchema, transaction)),
    }
  }

  async getTransactionsHistory(stripeCustomerId: string, page?: string, size = 10) {
    const result = await this.api.get<PaginatedCursor<CustomerHistoryDto>>(
      `${this.userFinanceBaseUrl}/customer/${stripeCustomerId}/history`,
      {
        params: {
          page,
          pageSize: size,
        },
      },
    )
    const pageData = result.data
    return {
      ...pageData,
      data: pageData.data.map((transaction) => LoggerService.parseApiDto(CustomerHistoryDtoSchema, transaction)),
    }
  }

  async getStats(period: string): Promise<FinanceStatisticsDto> {
    const result = await this.api.get<FinanceStatisticsDto>(`${this.userFinanceBaseUrl}/transactions/overview`, {
      params: {
        period,
      },
    })
    return LoggerService.parseApiDto(FinanceStatisticsDtoSchema, result.data)
  }

  async exportTransactions(startDate: string, endDate: string): Promise<string> {
    const result = await this.api.post<string>(
      `${this.userFinanceBaseUrl}/export-transactions?startDate=${startDate}&endDate=${endDate}`,
    )
    return result.data
  }

  async exportMonthlyInvoices(yearMonth: string): Promise<{ blob: Blob; filename: string | undefined }> {
    const result = await this.api.post<Blob>(
      `${this.userFinanceBaseUrl}/export-transactions/zip?yearMonth=${yearMonth}`,
      undefined,
      {
        responseType: "blob",
        headers: {
          Accept: "application/octet-stream",
        },
      },
    )
    const disposition = result.headers["content-disposition"] as string | undefined
    const filename = disposition?.match(/filename="?([^"]+)"?/)?.[1]

    return {
      blob: result.data,
      filename,
    }
  }

  async exportPayoutTransactions(payoutId: string): Promise<string> {
    const result = await this.api.get<string>(`${this.userFinanceBaseUrl}/payouts/${payoutId}/export`)
    return result.data
  }

  async getStripeAccountErrors() {
    const result = await this.api.get<StripeErrorsDto | null>(`${this.userStripeErrorsUrl}`)
    return result.status === 204 ? null : result.data
  }

  async getPaymentAuthenticationUrl(
    returnUrl: string,
    programSubscriptionId?: string,
    programBundleSubscriptionId?: string,
    boxSubscriptionId?: string,
  ): Promise<string> {
    const result = await this.api.post<string>(`${this.userStripePaymentRetryUrl}`, {
      programSubscriptionId,
      programBundleSubscriptionId,
      boxSubscriptionId,
      returnUrl,
    })
    return result.data
  }

  async getTransactionPdf(transactionId: string): Promise<Blob> {
    const result = await this.api.get<Blob>(`${this.userFinanceBaseUrl}/transactions/${transactionId}/pdf`, {
      responseType: "blob",
    })
    return result.data
  }
}
